/**
 * screenContextFor — PURE route → agent screen-context resolver
 * (global screen-contextual chat · FIX 3+4 · 2026-05-18b).
 *
 * Kept in its own Firebase/React-free module so the Node test runner can
 * lock the contract directly (same discipline as lib/localChat). The
 * GlobalChatDock re-exports this and feeds the result into the ONE agent
 * so it answers/acts about whichever screen the user is on — NOT a
 * hardcoded "Project Conversation" everywhere.
 */
import type { ScreenCtx } from './localChat';

export function screenContextFor(pathname: string): ScreenCtx {
  const raw = pathname || '/';
  const p = raw.toLowerCase();

  // Match on the lower-cased path for routing, but take the entity id
  // from the ORIGINAL path so ids keep their case (e.g. VP656, AV-C-0007).
  const proj = raw.match(/^\/project\/([^/]+)/i);
  if (proj) {
    return {
      screen: 'project',
      screenLabel: 'this project',
      screenEntityId: decodeURIComponent(proj[1]),
    };
  }
  if (p === '/' || p === '' || p.startsWith('/home')) {
    return { screen: 'home', screenLabel: 'Home' };
  }
  if (p.startsWith('/leads')) {
    return { screen: 'leads', screenLabel: 'Lead Inbox' };
  }
  if (p.startsWith('/clients')) {
    return { screen: 'clients', screenLabel: 'Clients' };
  }
  // PARTNER SPRINT — the Partner Portfolio carries the partner id; the
  // /partners list does not. Both resolve to a partner screen context.
  const partner = raw.match(/^\/partner\/([^/]+)/i);
  if (partner) {
    return {
      screen: 'partner',
      screenLabel: 'this partner',
      screenEntityId: decodeURIComponent(partner[1]),
    };
  }
  if (p.startsWith('/partners')) {
    return { screen: 'partners', screenLabel: 'Partners' };
  }
  if (p.startsWith('/ancillary')) {
    return { screen: 'ancillary', screenLabel: 'Ancillary services' };
  }
  if (p.startsWith('/money')) {
    return { screen: 'money', screenLabel: 'Money' };
  }
  if (p.startsWith('/workload')) {
    return { screen: 'workload', screenLabel: 'Workload' };
  }
  if (p.startsWith('/queue')) {
    return { screen: 'queue', screenLabel: 'Review Queue' };
  }
  if (p.startsWith('/conversation')) {
    return { screen: 'conversation', screenLabel: 'Project Conversation' };
  }
  if (p.startsWith('/journey')) {
    return { screen: 'journey', screenLabel: 'your journey' };
  }
  if (p.startsWith('/report')) {
    return { screen: 'report', screenLabel: 'your report' };
  }
  if (p.startsWith('/admin-queue')) {
    return { screen: 'admin_queue', screenLabel: 'the admin action queue' };
  }
  if (p.startsWith('/settings')) {
    return { screen: 'settings', screenLabel: 'Settings' };
  }
  return { screen: 'general', screenLabel: 'the portal' };
}
